import { useState } from "react";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const valueProps = [
  "A 30-minute call with the person who'd actually build your system",
  "A short list of your top 1–3 bottlenecks, ranked by what they're costing you",
  "An honest yes/no on whether software can fix each one",
  "A fixed scope + price for the first build, in writing, within a week",
  "You leave with the plan either way — even if you don't hire us",
];

export function Inquiry({ recaptchaSiteKey }: { recaptchaSiteKey: string }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function getRecaptchaToken(): Promise<string> {
    if (!recaptchaSiteKey) {
      throw new Error("Form isn't ready yet — please refresh and try again.");
    }
    if (!window.grecaptcha) {
      throw new Error("Spam protection didn't load. Please refresh and try again.");
    }
    return new Promise((resolve, reject) => {
      window.grecaptcha!.ready(async () => {
        try {
          const token = await window.grecaptcha!.execute(recaptchaSiteKey, {
            action: "inquiry",
          });
          resolve(token);
        } catch (e) {
          reject(e);
        }
      });
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status.kind === "submitting") return;

    setStatus({ kind: "submitting" });

    try {
      const recaptchaToken = await getRecaptchaToken();

      const res = await fetch("/api/inquiry", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          message: message.trim(),
          recaptchaToken,
        }),
      });

      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };

      if (!res.ok || !body.ok) {
        throw new Error(body.error || "Something went wrong. Please try again.");
      }

      setStatus({ kind: "success" });
      setName("");
      setEmail("");
      setCompany("");
      setMessage("");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Submission failed.";
      setStatus({ kind: "error", message });
    }
  }

  const disabled = status.kind === "submitting";

  return (
    <section className="section inquiry" id="inquiry">
      <div className="container inquiry-grid">
        <div className="inquiry-pitch">
          <span className="eyebrow">Book your call</span>
          <h2 className="section-title">
            Show us your bottleneck. We&rsquo;ll show you the fix.
          </h2>
          <p className="section-sub">
            Tell us where your business is losing time, money, or sleep. We&rsquo;ll
            come back within one business day with a plan &mdash; even if it&rsquo;s
            not us. No pitch, no pressure.
          </p>

          <ul className="value-stack">
            {valueProps.map((v) => (
              <li key={v}>
                <span className="check" aria-hidden="true">✓</span>
                <span>{v}</span>
              </li>
            ))}
          </ul>

          <p className="risk-reversal">
            No sales pitch · No obligation · No fake urgency
          </p>
        </div>

        <form className="form-card" onSubmit={onSubmit} noValidate>
          <div className="form-row">
            <div className="field">
              <label htmlFor="f-name">Name</label>
              <input
                id="f-name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={200}
                disabled={disabled}
              />
            </div>
            <div className="field">
              <label htmlFor="f-email">Email</label>
              <input
                id="f-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={200}
                disabled={disabled}
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="f-company">
              Company <span className="optional">(optional)</span>
            </label>
            <input
              id="f-company"
              type="text"
              autoComplete="organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              maxLength={200}
              disabled={disabled}
            />
          </div>

          <div className="field">
            <label htmlFor="f-message">Where&rsquo;s your business losing time or money?</label>
            <textarea
              id="f-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              maxLength={5000}
              disabled={disabled}
              placeholder="e.g. We answer 30+ leads a week by hand and half go cold. Or our office manager is drowning in invoicing and AR..."
            />
          </div>

          <div className="form-foot">
            {status.kind === "success" && (
              <div className="form-msg ok" role="status">
                ✅ Got it. We&rsquo;ll be in touch within one business day. Check
                your inbox.
              </div>
            )}
            {status.kind === "error" && (
              <div className="form-msg err" role="alert">
                {status.message}
              </div>
            )}

            <button className="btn btn-primary btn-block" type="submit" disabled={disabled}>
              {disabled ? "Sending..." : (
                <>Get my free game plan <span className="arrow">→</span></>
              )}
            </button>

            <p className="captcha-note">
              Protected by reCAPTCHA &mdash; Google&rsquo;s{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy</a>
              {" & "}
              <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms</a> apply.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
